// A landscape signature pad (plan §6 step 5): Pointer Events on a canvas, 5 px ink, a baseline
// with an X, one Clear button. The ink is only a picture; the evidence is the StrokeRecorder's
// integer points (events spec §4). One pointer at a time, so a resting palm cannot scribble.
// Rejected: signature_pad (10 KB): it smooths and records floats we would have to re-quantise,
// and StrokeRecorder already owns the canonical format, so drawing is 40 lines of our own.
import { useEffect, useRef, type PointerEvent as ReactPointerEvent } from "react";
import { StrokeRecorder } from "@vavr/client";

export const INK_WIDTH = 5;

export interface SignaturePadProps {
  /** Receives the recorder once the pad has its size; the flow reads toStroke() at submit. */
  onRecorder: (r: StrokeRecorder) => void;
  onChange: (points: number, enough: boolean) => void;
  label: string;
  disabled?: boolean;
  /** Bump to clear from outside (the Clear button). */
  clearKey: number;
}

export function SignaturePad({ onRecorder, onChange, label, disabled, clearKey }: SignaturePadProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const recRef = useRef<StrokeRecorder | null>(null);
  const activePointer = useRef<number | null>(null);
  const last = useRef<{ x: number; y: number } | null>(null);

  // Size the backing store to the CSS box times the pixel ratio; the recorder works in CSS px.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const setup = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = Math.max(1, window.devicePixelRatio || 1);
      canvas.width = Math.round(rect.width * dpr);
      canvas.height = Math.round(rect.height * dpr);
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.lineWidth = INK_WIDTH;
        ctx.strokeStyle = getComputedStyle(canvas).color;
        ctx.fillStyle = ctx.strokeStyle;
      }
      const rec = new StrokeRecorder(rect.width, rect.height);
      recRef.current = rec;
      onRecorder(rec);
      onChange(0, false);
    };
    setup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearKey]);

  const point = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const onDown = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    if (disabled || activePointer.current !== null || !recRef.current) return;
    activePointer.current = e.pointerId;
    e.currentTarget.setPointerCapture(e.pointerId);
    const p = point(e);
    recRef.current.start(p.x, p.y, e.timeStamp);
    last.current = p;
    const ctx = e.currentTarget.getContext("2d");
    if (ctx) { ctx.beginPath(); ctx.arc(p.x, p.y, INK_WIDTH / 2, 0, Math.PI * 2); ctx.fill(); }
    report();
  };

  const onMove = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    if (e.pointerId !== activePointer.current || !recRef.current) return;
    const ctx = e.currentTarget.getContext("2d");
    // Coalesced events keep fast strokes smooth on 60 Hz panels.
    const events = (e.nativeEvent.getCoalescedEvents?.() ?? []).length ? e.nativeEvent.getCoalescedEvents() : [e.nativeEvent];
    const r = e.currentTarget.getBoundingClientRect();
    for (const ev of events) {
      const p = { x: ev.clientX - r.left, y: ev.clientY - r.top };
      recRef.current.move(p.x, p.y, ev.timeStamp);
      if (ctx && last.current) { ctx.beginPath(); ctx.moveTo(last.current.x, last.current.y); ctx.lineTo(p.x, p.y); ctx.stroke(); }
      last.current = p;
    }
    report();
  };

  const onUp = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    if (e.pointerId !== activePointer.current) return;
    activePointer.current = null;
    last.current = null;
  };

  const report = () => {
    const rec = recRef.current;
    if (rec) onChange(rec.points(), rec.isEnough());
  };

  return (
    <div className="pmk-pad">
      <canvas
        ref={canvasRef} className="pmk-pad__canvas" role="img" aria-label={label}
        onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}
      />
      <div className="pmk-pad__line" aria-hidden="true"><span className="pmk-pad__x">X</span></div>
    </div>
  );
}
